import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@hero/web-context';
import { BillingCheckoutReturnScreen } from '@hero/web-components';
import {
  useBillingApi,
  useBillingTransactionByGatewayPaymentIdApi,
} from '@hero/api-hooks';
import { useNav } from '@hero/web-hooks';

/**
 * Payment confirmation return target — `/billing/checkout/return`.
 *
 * Moyasar redirects users back here after hosted checkout (success
 * or failure). It appends `?id=<paymentId>&status=paid|failed` to
 * the URL itself; we use the `id` to look up the corresponding BTx
 * and poll until the webhook has materialised the row (or 12s
 * elapse, at which point we show a soft "still processing" state so
 * the user isn't stuck on a spinner if the webhook is delayed).
 *
 * BTx rows are written only by the webhook handler, so during the
 * initial poll the backend returns `{ transaction: null }` — that's
 * the "verifying" state. Once the BTx materialises it's already in
 * its terminal status (`success` or `failed`).
 *
 * State machine:
 *   - `verifying` — no BTx yet (webhook in flight)
 *   - `success`   — BTx.status === 'success'
 *   - `failed`    — BTx.status === 'failed' (or no `id` in URL)
 *   - `pending`   — 12s elapsed without a BTx materialising
 */
const POLL_INTERVAL_MS = 1500;
const POLL_CEILING_MS = 12000;

type ReturnState = 'verifying' | 'success' | 'failed' | 'pending';

export default function BillingCheckoutReturnPage() {
  const { t } = useTranslation('m-billing');
  const { setPageName } = usePageContext();
  const { navigate } = useNav();
  const [searchParams] = useSearchParams();

  // Moyasar URL params: `id` = payment id, `status` = paid|failed.
  // We trust `status` to short-circuit the "definitely-failed" case
  // (no need to poll a BTx for a cancelled checkout), but for paid
  // we still wait for our backend to confirm the BTx — the webhook
  // is the source of truth, not the URL.
  const moyasarPaymentId = searchParams.get('id') ?? undefined;
  const moyasarStatus = searchParams.get('status');

  useEffect(() => {
    setPageName(t('checkout.return.verifying.title'));
  }, [setPageName, t]);

  const txQuery = useBillingTransactionByGatewayPaymentIdApi(moyasarPaymentId);
  // `transaction` is `null` while we're waiting on the webhook to
  // create the BTx — the return type is `{ transaction: null } |
  // { transaction, billing }`. We do NOT throw on null; we just poll.
  const transaction = txQuery.data?.transaction ?? null;
  const billing = txQuery.data && 'billing' in txQuery.data ? txQuery.data.billing : undefined;
  const billingId = transaction?.billingId ?? billing?._id;
  // We only need the latest Billing snapshot for the success card
  // (amount, title). Disabled while we don't have a BTx yet.
  const billingQuery = useBillingApi(billingId);

  // 12s ceiling — flip from `verifying` to `pending` if BTx is still
  // unresolved by then. Tracked as a state flag so render stays in
  // sync with the timer.
  const [timedOut, setTimedOut] = useState(false);
  const startedAtRef = useRef<number>(Date.now());
  useEffect(() => {
    const elapsed = Date.now() - startedAtRef.current;
    if (elapsed >= POLL_CEILING_MS) {
      setTimedOut(true);
      return undefined;
    }
    const handle = setTimeout(() => setTimedOut(true), POLL_CEILING_MS - elapsed);
    return () => clearTimeout(handle);
  }, []);

  // Poll the BTx lookup while we're still in `verifying`. Stops as
  // soon as the BTx materialises (it's already terminal when it does
  // — BTx has no `pending` status) or the 12s ceiling fires.
  const txStatus = transaction?.status;
  const stillVerifying = useMemo(() => {
    if (!moyasarPaymentId) return false;
    if (moyasarStatus === 'failed' && !transaction) return false;
    if (timedOut) return false;
    return txStatus === undefined;
  }, [moyasarPaymentId, moyasarStatus, transaction, txStatus, timedOut]);

  const { refetch } = txQuery;
  useEffect(() => {
    if (!stillVerifying) return undefined;
    const handle = window.setInterval(() => {
      refetch();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [stillVerifying, refetch]);

  // Derive the screen state from URL + BTx status + timeout flag.
  // `moyasarStatus === 'failed'` short-circuits when the user clicks
  // "Cancel" on the hosted checkout — no BTx is created in that
  // case, so we'd otherwise sit on `verifying` until the ceiling.
  const state: ReturnState = useMemo(() => {
    if (!moyasarPaymentId && moyasarStatus !== 'failed') {
      // Hit the URL with no params at all — treat as failed so we
      // don't spin forever; gives the user a path back to billing.
      return 'failed';
    }
    if (moyasarStatus === 'failed' && !transaction) return 'failed';
    if (txStatus === 'success') return 'success';
    if (txStatus === 'failed') return 'failed';
    if (timedOut) return 'pending';
    return 'verifying';
  }, [moyasarPaymentId, moyasarStatus, transaction, txStatus, timedOut]);

  const errorReason = transaction?.gateway?.response?.message;

  const handleStartWorking = useCallback(() => {
    navigate('dashboard');
  }, [navigate]);
  const handleBackToBilling = useCallback(() => {
    navigate('billing');
  }, [navigate]);
  const handleTryAgain = useCallback(() => {
    navigate('billing_change_plan');
  }, [navigate]);
  const handleRefresh = useCallback(() => {
    setTimedOut(false);
    startedAtRef.current = Date.now();
    refetch();
  }, [refetch]);

  return (
    <BillingCheckoutReturnScreen
      state={state}
      billing={billingQuery.data ?? undefined}
      errorReason={errorReason}
      onStartWorking={handleStartWorking}
      onBackToBilling={handleBackToBilling}
      onTryAgain={handleTryAgain}
      onRefresh={handleRefresh}
    />
  );
}
